import {Button, Grid, MenuItem, Paper, TextField, Tooltip, useTheme} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import '../../style/SalesMarketing.css';
import * as Yup from 'yup';
import ProductCodeCreateModal from "./modals/ProductCodeCreateModal";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import {useNavigate} from "react-router-dom";
import {useRecoilState} from "recoil";
import {
    BILLING_MODES,
    CATEGORY_LIST,
    DEPOSIT_CODES_LIST,
    GUARANTEE_BILLING_MODE_REL,
    GUARANTEE_PAYMENT_METHOD_REL,
    GUARANTEE_PAYMENT_TIMING_REL,
    GUARANTEE_SCHEMES,
    INVOICE_RECIPIENTS,
    PAYMENT_DUE_DATES,
    PAYMENT_METHDOS,
    PAYMENT_TIMINGS,
    SEGMENT_LIST,
    SEGMENTS_NO_GUARANTEE
} from "../../service/SalesMarketingRecoil";
import {InfoRounded} from "@mui/icons-material";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {boolToString, floatToIntCurrency} from "../../component/Utils";
import TaxConfigurationAccordion from "../../component/TaxConfiguration/TaxConfigurationAccordion";
import {
    preselectSingleEntity,
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";

export enum GuaranteePaymentTimingCodes {
    STANDARD_CREDIT = 'STANDARD_CREDIT',
    FULL_CREDIT = 'FULL_CREDIT',
    NO_CREDIT = 'NO_CREDIT'
}

export enum PaymentMethodsValues {
    CREDIT_CARD = "J",
    AMEXSOF="A",
    DIRECT_DEBIT="D",
    BANK_TRANSFER="B"
}
export enum PaymentTimingsValues {
    PREPAID = "1",
    PICKUP = "2",
    DROPOFF="3"
} 

const ProductCodeCreatePage = () => {
    const theme = useTheme();
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const navigate = useNavigate();
    const productCodeActions = useProductCodeActions();
    const [modalOpen, setModalOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [segments] = useRecoilState(SEGMENT_LIST);
    const [categoriesMap] = useRecoilState(CATEGORY_LIST);
    const [paymentMethods] = useRecoilState(PAYMENT_METHDOS);
    const [guaranteeSchemes] = useRecoilState(GUARANTEE_SCHEMES);
    const [paymentDueDates] = useRecoilState(PAYMENT_DUE_DATES);
    const [paymentTimings] = useRecoilState(PAYMENT_TIMINGS);
    const [invoiceRecipients] = useRecoilState(INVOICE_RECIPIENTS);
    const [billingModes] = useRecoilState(BILLING_MODES);
    const [segmentsNoGuarantee] = useRecoilState(SEGMENTS_NO_GUARANTEE);
    const [guaranteePaymentMethodRel] = useRecoilState(GUARANTEE_PAYMENT_METHOD_REL);
    const [guaranteePaymentTimingRel] = useRecoilState(GUARANTEE_PAYMENT_TIMING_REL);
    const [guaranteeBillingModeRel] = useRecoilState(GUARANTEE_BILLING_MODE_REL);
    const [createdProductCode, setCreatedProductCode] = useState("");
    const {globalData} = useContext(GlobalContext);
    const [depositList] = useRecoilState(DEPOSIT_CODES_LIST);

    useEffect(() => {
        productCodeActions.listSegment();
        // productCodeActions.listCategory(); // Categories will be loaded dynamically based on tenant
        productCodeActions.listPaymentTimings();
        productCodeActions.listInvoiceRecipients();

    }, [])

    const initialValues = {
        tenantId: "",
        dealerId: "",
        status: 'Draft',
        productCode: '',
        name: '',
        segment: '',
        category: '',
        description: '',
        paymentMethod: PaymentMethodsValues.CREDIT_CARD,
        paymentDueDate: '',
        guarantee: GuaranteePaymentTimingCodes.NO_CREDIT,
        billingMode: '',
        startDate: null,
        endDate: null,
        firstRecipient: '',
        subsequentRecipient: '',
        paymentTiming: PaymentTimingsValues.PREPAID,
        fastLine: false,
        tax_configuration: {
            railwayStationTax: "S",
            roadTax: "S",
            airportTax: "S",
            airportRailwayStationTax: "",
            roadTaxDailyValue: "",
            reservedPricing: ""
        },
        preAuthCode:''
    };

    const validationSchema = Yup.object({
        status: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        productCode: Yup.string().nullable(),
        name: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        segment: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        category: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        description: Yup.string().nullable(),
        paymentMethod: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        paymentDueDate: Yup.string().nullable(),
        guarantee: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        billingMode: Yup.string().nullable(),
        startDate: Yup.date().nullable().test("testStartDate", "*" + t('salesmarketing:product.createForm.datesCheck'), (value, context) => {
            return !value || !context.parent.endDate || (value <= context.parent.endDate);
        }).typeError(t('product.createForm.invalidDate')),
        endDate: Yup.date().nullable().test("testEndDate", "*" + t('salesmarketing:product.createForm.datesCheck'), (value, context) => {
            return !value || !context.parent.startDate || (value >= context.parent.startDate);
        }).typeError(t('product.createForm.invalidDate')),
        firstRecipient: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        subsequentRecipient: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        paymentTiming: Yup.string().required("*" + t('salesmarketing:product.createForm.requiredCheck')),
        tax_configuration :Yup.object({
            airportRailwayTaxPercentage: Yup.number().min(0).max(100)
        })
    });

    const onSubmit = async (data: any) => {
       
        let payload = {...data, fastLine: boolToString(data.fastLine),  tax_configuration : {
                ...data.tax_configuration, roadTaxDailyValue : data.tax_configuration.roadTaxDailyValue ? floatToIntCurrency(data.tax_configuration.roadTaxDailyValue) : ''
            }};
        if (data.segment !== 'TOP')
            delete payload.fastLine;

        let result = await productCodeActions.createProductCode(payload);
        if (result && result?.status === 'OK') {
            setIsSuccess(true);
            setCreatedProductCode(result?.result?.productCode);
            setModalOpen(true);
        }
    }

    const methods = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });

    const {handleSubmit, control, setValue, watch} = methods;

    useEffect(() => {
        preselectSingleEntity(globalData, setValue);
    }, [globalData, setValue]);

    useEffect(() => {
        let tenantId = watch('tenantId');
        let dealerId = watch('dealerId');
        emptyFields();
        if(tenantId){
            productCodeActions.listCategory(tenantId);
            productCodeActions.listBillingModes(tenantId);
            setValue('category', '');
        }
        if(tenantId && dealerId){
            productCodeActions.listPaymentDueDates(tenantId, dealerId);
            productCodeActions.listGuaranteeSchemes(tenantId, dealerId);
            productCodeActions.listPaymentMethods(tenantId, dealerId);
            productCodeActions.getPreAuthorizationList(tenantId, dealerId);
        }
    }, [watch('tenantId'), watch('dealerId')]);

    function emptyFields() {
        setValue("paymentDueDate", "")
        setValue("preAuthCode", "")
    }

    return (
        <>
            <SectionHeader title={t('salesmarketing:product.createHeader.title')}
                           subtitle={t('salesmarketing:product.createHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <FormProvider {...methods} >
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Grid container rowSpacing={4}>
                            <Grid container item spacing={1}>
                            <UserLegalEntitiesOrDealersInputGroup
                                control={control}
                            />
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"status"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.statusField')}
                                                InputProps={{
                                                    endAdornment: <Tooltip
                                                        title={t('salesmarketing:product.createForm.statusTooltip')}><InfoRounded
                                                        color="primary"/></Tooltip>
                                                }}
                                                disabled
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"productCode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.productCodeField')}
                                                InputProps={{
                                                    endAdornment: <Tooltip
                                                        title={t('salesmarketing:product.createForm.productCodeTooltip')}><InfoRounded
                                                        color="primary"/></Tooltip>
                                                }}
                                                disabled
                                                placeholder={t('salesmarketing:product.createForm.productCodePlaceholder')}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={6}/>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"name"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.nameField')}
                                                placeholder={t('salesmarketing:product.createForm.namePlaceholder')}
                                                required
                                                inputProps={{maxLength: 30}}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"segment"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.segmentField')}
                                                select
                                                required
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e)
                                                    setValue('category', '')
                                                    if (segmentsNoGuarantee.includes(e.target.value)) {
                                                        setValue('guarantee', GuaranteePaymentTimingCodes.NO_CREDIT);
                                                        setValue('billingMode', '');
                                                    }
                                                }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {segments?.map((s) => (
                                                    <MenuItem key={s.segmentCode} value={s.segmentCode}>
                                                        {s.segmentName}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"category"}
                                        render={({field, fieldState}) => {
                                            const selectedTenantId = watch('tenantId');
                                            const availableCategories = categoriesMap[selectedTenantId] || [];
                                            const isCategoryDisabled = !selectedTenantId || availableCategories.length === 0;

                                            return (
                                                <TextField
                                                    fullWidth
                                                    label={t('salesmarketing:product.createForm.categoryField')}
                                                    select
                                                    required
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    disabled={isCategoryDisabled}
                                                >
                                                    <MenuItem key={""} value={""}/>
                                                    {availableCategories.filter(c => c.segmentCode === watch("segment"))?.map((c) => (
                                                        <MenuItem key={c.categoryCode} value={c.categoryCode}>
                                                            {c.categoryLabel}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            );
                                        }}/>
                                </Grid>
                                {/*{(watch('segment') === 'TOP') &&
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"fastLine"}
                                        render={({field, fieldState}) => (
                                            <FormControlLabel
                                                sx={{mt: 3}}
                                                label={t('salesmarketing:product.createForm.fastLine')}
                                                control={<Checkbox
                                                    color={"accent"}
                                                    sx={{color: theme.palette.accent.main}}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                />}
                                            />
                                        )}/>
                                </Grid>
                            }*/}
                                <Grid item xs={6}>
                                    <Controller
                                        control={control}
                                        name={"description"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.descriptionField')}
                                                placeholder={t('salesmarketing:product.createForm.descriptionPlaceholder')}
                                                inputProps={{maxLength: 120}}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"startDate"}
                                        render={({field, fieldState}) => (
                                            <DatePicker
                                                label={t('salesmarketing:product.createForm.startDateField')}
                                                value={field.value}
                                                onChange={field.onChange}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}/>
                                                }
                                                inputFormat={"L"}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"endDate"}
                                        render={({field, fieldState}) => (
                                            <DatePicker
                                                label={t('salesmarketing:product.createForm.endDateField')}
                                                value={field.value}
                                                onChange={field.onChange}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}/>
                                                }
                                                inputFormat={"L"}
                                            />
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"preAuthCode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                name="preAuthCode"
                                                label={t('salesmarketing:product.deposit')}
                                                select
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {depositList?.map((a) => (
                                                    <MenuItem key={a.id} value={a.id}>
                                                        {a.description}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"guarantee"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.guaranteeField')}
                                                select
                                                required
                                                disabled={watch('segment') === 'RET'}
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    if(e.target.value === GuaranteePaymentTimingCodes.STANDARD_CREDIT || e.target.value === GuaranteePaymentTimingCodes.FULL_CREDIT){
                                                        setValue('paymentTiming',PaymentTimingsValues.DROPOFF);
                                                        setValue('paymentMethod',PaymentMethodsValues.BANK_TRANSFER)
                                                    }
                                                    if (e.target.value === GuaranteePaymentTimingCodes.NO_CREDIT) {
                                                        setValue('paymentTiming',PaymentTimingsValues.PREPAID)
                                                        setValue('paymentMethod',PaymentMethodsValues.CREDIT_CARD);
                                                    }          
                                                   
                                                    let billingModesCodes = guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes;
                                                    if (billingModesCodes && billingModesCodes?.length === 1) {
                                                        setValue('billingMode', billingModesCodes[0]);
                                                    } else {
                                                        setValue('billingMode', '');
                                                    }
                                                }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {guaranteeSchemes?.map((s) => (
                                                    <MenuItem key={s.label} value={s.value}>
                                                        {s.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentMethod"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentMethodField')}
                                                select
                                                required
                                                disabled={!watch("guarantee")}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {guaranteePaymentMethodRel?.find(g => g.code === watch("guarantee"))?.paymentMethodCodes.map((method) => {
                                                    let pm = paymentMethods.find(pm => pm.value === method);
                                                    return pm ? <MenuItem key={pm.label} value={pm.value}>
                                                        {pm.label}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentDueDate"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentDueDateField')}
                                                select
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {paymentDueDates?.map((s) => (
                                                    <MenuItem key={s.label} value={s.value}>
                                                        {s.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"billingMode"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.billingModeField')}
                                                select
                                                disabled={!watch("guarantee") || guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes?.length === 1}
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {guaranteeBillingModeRel?.find(b => b.code === watch("guarantee"))?.billingModeCodes.map((mode) => {
                                                    let bm = (billingModes[watch('tenantId')] || []).find(bm => bm.value === mode);
                                                    return bm ? <MenuItem key={bm.label} value={bm.value}>
                                                        {bm.label}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"firstRecipient"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.firstRecipientField')}
                                                select
                                                required
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {invoiceRecipients?.map((r) => (
                                                    <MenuItem key={r.id} value={r.id}>
                                                        {r.recipientType}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"subsequentRecipient"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.subsequentRecipientField')}
                                                select
                                                required
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                <MenuItem key={""} value={""}/>
                                                {invoiceRecipients?.map((r) => (
                                                    <MenuItem key={r.id} value={r.id}>
                                                        {r.recipientType}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={3}>
                                    <Controller
                                        control={control}
                                        name={"paymentTiming"}
                                        render={({field, fieldState}) => (
                                            <TextField
                                                fullWidth
                                                label={t('salesmarketing:product.createForm.paymentTimingField')}
                                                select
                                                required
                                                disabled={!watch('guarantee') || guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.length === 0 || guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.length === 1 }
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            >
                                                {guaranteePaymentTimingRel?.find(b => b.code === watch("guarantee"))?.paymentTimingCodes?.map((timing) => {
                                                    let pt = paymentTimings.find(pt => pt.id === timing);
                                                    return pt ? <MenuItem key={pt.id} value={pt.id}>
                                                        {pt.value}
                                                    </MenuItem> : null;
                                                })}
                                            </TextField>
                                        )}/>
                                </Grid>
                                <Grid item xs={12} sx={{marginTop: '50px'}}>
                                    <TaxConfigurationAccordion prefix={'tax_configuration'}/>
                                </Grid>
                            </Grid>
                            <Grid container item spacing={2}>
                                <Grid item xs={6}/>
                                <Grid item xs={3}>
                                    <Button fullWidth variant="outlined" size="medium" onClick={() => {
                                        navigate("/salesmarketing/productcodelist")
                                    }}>
                                        {t('salesmarketing:product.buttons.cancel')}
                                    </Button>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button fullWidth color="accent" size="medium"
                                            type="submit">{t('salesmarketing:product.buttons.confirm')}</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
            </Paper>
            <ProductCodeCreateModal open={modalOpen} setOpen={setModalOpen} isSuccess={isSuccess}
                                    createdProductCode={createdProductCode}/>
        </>
    );
}

export default ProductCodeCreatePage;